/**
 * Resolves a small subset of JSONPath expressions (as used by the backend's schedule
 * overrides, e.g. "$.vehicle_state.brake[*].brake_lining") to the 1-based line numbers
 * where the corresponding fields are rendered in a 2-space-indented pretty-printed JSON
 * string (the same format produced by `JSON.stringify(value, null, 2)`).
 *
 * This intentionally supports only the JSONPath subset produced by the backend's
 * vehicle YAML overrides: dot-separated object keys and `[*]` / `[n]` array indexers.
 * It does not implement the full JSONPath specification.
 *
 * Implementation approach: rather than parsing the pretty-printed text back into paths
 * (which is ambiguous for arrays of primitives/objects), this re-serializes the parsed
 * JSON value itself, line by line, recording which line each concrete path resolves to.
 * As long as the content was formatted with `JSON.stringify(value, null, 2)`, the
 * resulting line numbers line up exactly with what is displayed in the editor.
 */

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

interface PathSegment {
    key: string;
}

interface IndexSegment {
    index: number | '*';
}

type Segment = PathSegment | IndexSegment;

const isIndexSegment = (segment: Segment): segment is IndexSegment =>
    Object.prototype.hasOwnProperty.call(segment, 'index');

/**
 * Parses a JSONPath string like "$.a.b[0].c" or "$.a[*].b" into a list of segments.
 */
const parseJsonPath = (jsonPath: string): Segment[] => {
    const withoutRoot = jsonPath.replace(/^\$\.?/, '');
    const tokens = withoutRoot.match(/[^.[\]]+|\[[^\]]*\]/g) ?? [];

    return tokens.map((token) => {
        if (token.startsWith('[')) {
            const inner = token.slice(1, -1);
            return { index: inner === '*' ? '*' : Number(inner) };
        }
        return { key: token };
    });
};

/**
 * Expands a parsed path into every concrete (wildcard-free) path it can match against
 * the given JSON value, represented as arrays of string/number keys.
 */
const expandConcretePaths = (value: JsonValue, segments: Segment[]): (string | number)[][] => {
    if (segments.length === 0) {
        return [[]];
    }

    const [head, ...rest] = segments;

    if (isIndexSegment(head)) {
        if (!Array.isArray(value)) {
            return [];
        }
        const indices = head.index === '*' ? value.map((_, i) => i) : [head.index];
        return indices.flatMap((i) => {
            if (value[i] === undefined) return [];
            return expandConcretePaths(value[i], rest).map((tail) => [i, ...tail]);
        });
    }

    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return [];
    }
    const child = (value as Record<string, JsonValue>)[head.key];
    if (child === undefined) {
        return [];
    }
    return expandConcretePaths(child, rest).map((tail) => [head.key, ...tail]);
};

/**
 * Walks the JSON value the same way `JSON.stringify(value, null, 2)` would serialize it,
 * recording the 1-based line number at which each node starts. Returns a map from a
 * stringified concrete path (JSON-encoded array) to its line number.
 */
const buildPathToLineMap = (value: JsonValue): Map<string, number> => {
    const pathToLine = new Map<string, number>();
    let currentLine = 1;

    const encode = (path: (string | number)[]): string => JSON.stringify(path);

    const walk = (node: JsonValue, path: (string | number)[]): void => {
        pathToLine.set(encode(path), currentLine);

        if (Array.isArray(node)) {
            if (node.length === 0) return;
            node.forEach((item, index) => {
                currentLine += 1;
                walk(item, [...path, index]);
            });
            currentLine += 1; // closing bracket line
        } else if (typeof node === 'object' && node !== null) {
            const keys = Object.keys(node);
            if (keys.length === 0) return;
            keys.forEach((key) => {
                currentLine += 1;
                walk((node as Record<string, JsonValue>)[key], [...path, key]);
            });
            currentLine += 1; // closing brace line
        }
        // Primitives occupy a single line already accounted for by the increment before `walk`.
    };

    walk(value, []);
    return pathToLine;
};

/**
 * Resolves a list of (possibly wildcarded) JSONPath expressions against a JSON value that
 * will be rendered with `JSON.stringify(value, null, 2)`, returning the set of 1-based line
 * numbers that correspond to the overridden fields.
 */
export const resolveOverriddenLines = (jsonContent: string, jsonPaths: string[]): Set<number> => {
    const lineNumbers = new Set<number>();

    let parsed: JsonValue;
    try {
        parsed = JSON.parse(jsonContent);
    } catch {
        return lineNumbers;
    }

    const pathToLine = buildPathToLineMap(parsed);

    jsonPaths.forEach((jsonPath) => {
        const segments = parseJsonPath(jsonPath);
        const concretePaths = expandConcretePaths(parsed, segments);
        concretePaths.forEach((concretePath) => {
            const line = pathToLine.get(JSON.stringify(concretePath));
            if (line !== undefined) {
                lineNumbers.add(line);
            }
        });
    });

    return lineNumbers;
};

/**
 * Snapshot of the tagged (overridden) lines at the moment the payload was loaded into the
 * editor: the 1-based line number mapped to the exact text of that line at that time.
 *
 * This is used to track tags per-line rather than globally, so a tag is only dropped once the
 * user actually edits the specific line it belongs to, instead of clearing every tag whenever
 * any part of the text area is touched.
 */
export type OverriddenLinesSnapshot = Map<number, string>;

/**
 * Builds the initial per-line snapshot for a set of overridden JSON paths against the given
 * (freshly loaded, not-yet-edited) JSON content.
 */
export const buildOverriddenLinesSnapshot = (jsonContent: string, jsonPaths: string[]): OverriddenLinesSnapshot => {
    const lineNumbers = resolveOverriddenLines(jsonContent, jsonPaths);
    const lines = jsonContent.split('\n');
    const snapshot: OverriddenLinesSnapshot = new Map();

    lineNumbers.forEach((lineNumber) => {
        const lineText = lines[lineNumber - 1];
        if (lineText !== undefined) {
            snapshot.set(lineNumber, lineText);
        }
    });

    return snapshot;
};

/**
 * Re-derives the currently-active tagged line numbers by comparing the current content's
 * lines against the original snapshot: a line stays tagged only while its text is unchanged
 * from when it was snapshotted. Lines that were removed (content now has fewer lines) are
 * dropped as well.
 */
export const getActiveOverriddenLines = (jsonContent: string, snapshot: OverriddenLinesSnapshot): Set<number> => {
    const lines = jsonContent.split('\n');
    const activeLines = new Set<number>();

    snapshot.forEach((originalLineText, lineNumber) => {
        if (lines[lineNumber - 1] === originalLineText) {
            activeLines.add(lineNumber);
        }
    });

    return activeLines;
};
